/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { isNonCurvedLine } from '#utils/PRIVATE_chartUtils/utils';
import { getCmdsForLine, Point } from '#utils/PRIVATE_visSVGUtils';
import { styles } from '#PRIVATE_Chart/themes/ChartStyles.css';
import { Property } from 'csstype';
import { AllLineType } from '#utils/UNSAFE_visTypes/common/common.types';
import { classNames } from '#utils/UNSAFE_classNames';

type LineSeriesProps = {
  id?: number | string;
  color: ColorProps['color'];
  lineStyle?: 'dotted' | 'dashed' | 'solid';
  lineColor?: Property.Color;
  lineWidth?: number;
  lineType?: AllLineType;
  isHorizontal: boolean;
  lineSegmentCoords: Point[][];
  isHighlighted: boolean;
  seriesIndex: number;
  groupWidth?: number;
  isRtl?: boolean;
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency';
  plotAreaClipPathId?: string;
  variant?: 'normal' | 'refLine';
  accessibleLabel?: string;
  activeId?: string;
};

export function LineSeries({
  color,
  isHorizontal,
  lineType = 'straight',
  lineStyle,
  lineWidth = 3,
  lineColor,
  isHighlighted,
  lineSegmentCoords,
  seriesIndex,
  groupWidth,
  isRtl,
  timeAxisType,
  plotAreaClipPathId,
  variant = 'normal',
  accessibleLabel,
  activeId,
  id
}: LineSeriesProps) {
  const style = {
    stroke: lineColor || color,
    strokeDasharray: lineStyle === 'dotted' ? 3 : lineStyle === 'dashed' ? 6 : undefined,
    strokeWidth: lineWidth,
    fill: 'none'
  };
  return variant === 'refLine' ? (
    <>
      {lineSegmentCoords.map((value: Point[]) => {
        if (value.length < 2) {
          return;
        }
        const cmds = getCmdsForLine(value, lineType, isHorizontal, groupWidth, isRtl, timeAxisType);
        return isNonCurvedLine(lineType) ? (
          <g clip-path={`url(#${plotAreaClipPathId})`} aria-label={accessibleLabel} id={activeId}>
            {cmds.split(' ').map((cmd) => (
              <polyline
                points={cmd}
                style={style}
                shape-rendering={'crispEdges'}
                class={classNames([
                  isHighlighted ? undefined : styles.dimmed,
                  variant === 'refLine' ? styles.refLine : undefined
                ])}
                data-oj-private-ref-obj-id={id}
                data-oj-private-object={'refLine'}
              />
            ))}
          </g>
        ) : lineType === 'curved' ? (
          <path
            aria-label={accessibleLabel}
            id={activeId}
            d={cmds}
            clip-path={`url(#${plotAreaClipPathId})`}
            style={style}
            shape-rendering={'crispEdges'}
            class={classNames([
              isHighlighted ? undefined : styles.dimmed,
              variant === 'refLine' ? styles.refLine : undefined
            ])}
            data-oj-private-ref-obj-id={id}
            data-oj-private-object={'refLine'}
          />
        ) : undefined;
      })}
    </>
  ) : (
    <>
      {lineSegmentCoords.map((value: Point[]) => {
        if (value.length < 2) {
          return;
        }
        const cmds = getCmdsForLine(value, lineType, isHorizontal, groupWidth, isRtl, timeAxisType);
        return isNonCurvedLine(lineType) ? (
          <g clip-path={`url(#${plotAreaClipPathId})`} aria-label={accessibleLabel} id={activeId}>
            {cmds.split(' ').map((cmd) => (
              <polyline
                points={cmd}
                style={style}
                class={classNames([isHighlighted ? undefined : styles.dimmed])}
                data-oj-private-series-index={seriesIndex}
                data-oj-private-object={'line'}
              />
            ))}
          </g>
        ) : lineType === 'curved' ? (
          <path
            d={cmds}
            aria-label={accessibleLabel}
            id={activeId}
            clip-path={`url(#${plotAreaClipPathId})`}
            style={style}
            class={classNames([isHighlighted ? undefined : styles.dimmed])}
            data-oj-private-series-index={seriesIndex}
            data-oj-private-object={'line'}
          />
        ) : undefined;
      })}
    </>
  );
}
